<template>
  <div class="reorder-page">
    <h1 class="page-title">Reorder</h1>

    <div class="reorder-page-header">
      <search-input-field
        v-model="searchValue"
        :change="searchValueChange"
        class="search-input"
        placeholder="Search by product"
      />

      <sorting-options
        :options="sortingOptions"
        :is-mobile="isMobile"
        :hide-label="isMobile"
        no-pagination
        no-tracking
      />
    </div>

    <template v-if="!loading">
      <template v-if="reorders && !searchMode">
        <template v-for="item in reorders">
          <reorder-item :reorder="item" :on-follow-click="handleFollowClick" />
          <more-from-brand
            :brand-name="item.brand.name"
            :brand-id="item.brand.id"
            :brand-vanity-url-name="item.brand.vanity_url_name"
          />
        </template>
      </template>

      <template>
        <reorder-search-result :search-result="searchResult" v-if="searchMode && !loading" />
        <div v-if="searchMode && !loading && !searchResult.length">No results</div>
      </template>

      <div class="pagination-container" v-if="pagination.numberOfPages > 1 && !loading">
        <pagination
          :active-page="pagination.page"
          :limit="pagination.limit"
          :on-click="getReorders"
          :total="pagination.totalCount"
          :number-of-pages="pagination.numberOfPages"
          class="no-padding-right"
        />
      </div>

      <bulletin-button v-if="reorders && !searchMode" class="margin-top-40" to="/dashboard/retailer/orders"
        >See all orders
      </bulletin-button>
    </template>

    <loader-static class="flex justify-center align-center margin-top-24" v-if="loading"></loader-static>
  </div>
</template>

<style scoped lang="scss">
@import "@/assets/sass/base/_mixins.scss";
@import "@/assets/sass/base/_vars.scss";
.reorder-page {
  padding: 40px;
  min-height: calc(100vh - 122px);
  .page-title {
    font-size: 48px;
  }

  .reorder-page-header {
    margin-top: 16px;
    margin-bottom: 40px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .search-input {
      width: 322px;
      margin-bottom: 0 !important;
    }
  }

  .pagination-container {
    padding-block: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-block: 1px solid $color-grey;
  }

  @include respond-to("medium") {
    padding: 16px;
    overflow: hidden;

    .page-title {
      font-size: 32px;
    }
  }

  @include respond-to("small") {
    .reorder-page-header {
      .search-input {
        width: 100%;
      }
    }
  }
}
</style>

<script>
import OrderApi from "../../../services/api/orderApi";
import FavoriteApi from "../../../services/api/favoritesApi";
import ReorderItem from "./reorderItem";
import { algoliaBrandHelper } from "../../../components/mixin/search/algoliaBrandHelper";
import _debounce from "lodash/debounce";
import { breakPointsHelper } from "../../../components/mixin/breakPointsHelpers";
import { reorderPageHelper } from "./reorderPageHelper";
import ReorderVariantTile from "./reorderVariantTile";
import ReorderSearchResult from "./reorderSearchResult";
import { paramsHelper } from "../../../components/mixin/paramsHelper";
import MoreFromBrand from "./moreFromBrand";

const OrderApiService = new OrderApi();
const FavoriteApiService = new FavoriteApi();

export default {
  mixins: [algoliaBrandHelper, breakPointsHelper, reorderPageHelper, paramsHelper],
  components: {MoreFromBrand, ReorderSearchResult, ReorderItem, ReorderVariantTile },
  data() {
    return {
      reorders: [],
      total: 0,
      hasOrders: true,
      loading: true,
      searchValue: this.$route.query?.searchTerm || "",
      searchResult: [],
      defaultSorting: "DESC",
      pagination: {
        limit: 5,
        page: 1,
        total: null,
        numberOfPages: 0,
      },
      sortingOptions: [
        {
          name: "Newest to Oldest",
          value: "DESC",
        },
        {
          name: "Oldest to newest",
          value: "ASC",
        },
      ],
    };
  },
  methods: {
    getReorders,
    setFavoriteState,
    handleFollowClick,
    getFavoriteProducts,
    setPage,
    searchValueChange,
  },
  computed: {
    isMobile() {
      return this.underBreakpoint("sm");
    },
    searchMode() {
      return !!this.searchValue;
    },
  },
  watch: {
    searchValue: _debounce(async function (value) {
      this.updateURLParams("searchTerm", value);
      this.setPage(1);
      await this.getReorders(1);
    }, 500),
    "$route.query.sortBy": async function () {
      await this.getReorders();
    },
  },

  async beforeMount() {
    await Promise.all([this.getFavoriteProducts(), this.getFavoriteBrands()]);

    if (!this.$route.query.sortBy) {
      this.updateURLParams("sortBy", this.defaultSorting);
    } else {
      await this.getReorders();
    }
  },
};

async function getReorders(page) {
  this.setPage(page || 1);
  this.$loader.$emit("trigger", true);
  this.loading = true;
  try {
    const { data } = await OrderApiService.getReorders(
      this.$route.query.sortBy,
      this.pagination.page,
      this.pagination.size,
      this.searchValue
    );

    this.pagination.total = data.total;
    this.pagination.numberOfPages = Math.ceil(this.pagination.total / this.pagination.limit);

    if (this.searchValue) {
      this.reorders = [];
      this.searchResult = data.reorders || [];
    } else {
      this.searchResult = [];
      this.reorders = data.reorders;
    }

    this.setFavoriteState();
  } catch (error) {
    if (error.response) {
      this.$swal("Attention", error.response.data.error_message);
    } else {
      this.$bugsnagClient.notify(new Error(error));
    }
  } finally {
    this.loading = false;
    this.$loader.$emit("trigger", false);
  }
}

function setFavoriteState() {
  this.reorders =
    this.reorders.map((item) => {
      return { ...item, brand: { ...item.brand, isBrandFavorite: this.favoriteBrandIDsList.includes(item.brand.id) } };
    }) || [];
}

async function handleFollowClick() {
  await this.getFavoriteBrands();
  this.setFavoriteState();
}

async function getFavoriteProducts() {
  await this.$utils.getFavoriteProducts.call(this);
}

function setPage(page) {
  this.pagination.page = parseInt(page);
  this.updateURLParams("page", page);
}

function searchValueChange() {
  this.loading = true;
}
</script>
